import React from "react";
import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import RudrasLogo from "./RudrasLogo";
import { AlertTriangle, RefreshCw } from "lucide-react";

/**
 * AuthErrorBoundary — defensive shell around /login, /register, and
 * /internal-admin/login. If any descendant component throws during render
 * (e.g. a future regression that passes a non-renderable error payload
 * to a child), this surfaces a friendly fallback instead of the blank
 * white screen React would otherwise leave on the page.
 *
 * SCOPE: render-error insurance only. It deliberately does NOT touch auth
 * logic, JWT handling, session preservation, tenant binding, or backend
 * validation — those are stabilized zones.
 */
export default class AuthErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Keep this lightweight — we don't want to mask the failure during
    // development. The browser console keeps the original stack trace.
    // eslint-disable-next-line no-console
    console.error("[AuthErrorBoundary] caught render error:", error, info?.componentStack);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const message =
      (this.state.error && (this.state.error.message || String(this.state.error))) ||
      "Something went wrong while loading this page.";

    return (
      <div
        className="min-h-screen grid place-items-center p-6 bg-background"
        data-testid="auth-error-boundary"
      >
        <div className="w-full max-w-md text-center space-y-6">
          <div className="flex justify-center">
            <RudrasLogo size="lg" />
          </div>

          <div className="flex justify-center">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-rose-50 border border-rose-200 text-rose-700 text-[11px] uppercase tracking-wider">
              <AlertTriangle className="w-3.5 h-3.5" />
              Something interrupted this page
            </span>
          </div>

          <div className="space-y-2">
            <h1 className="font-display text-3xl font-semibold">We couldn't load the form.</h1>
            <p className="text-sm text-muted-foreground">
              A temporary glitch stopped the page from rendering. Your account is safe — nothing was submitted.
              Try again, or head back to the home page.
            </p>
            <p
              className="text-[11px] font-mono text-muted-foreground/80 break-words"
              data-testid="auth-error-message"
            >
              {message}
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-2 justify-center">
            <Button
              onClick={this.handleReset}
              className="bg-primary hover:bg-primary/90"
              data-testid="auth-error-retry"
            >
              <RefreshCw className="w-4 h-4 mr-1.5" /> Try again
            </Button>
            <Link to="/" data-testid="auth-error-home">
              <Button variant="outline">Back to home</Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }
}
